/**
 * UI Slice
 * Manages shared UI states like drawer visibility, dialogs, etc.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * Load icon sprite atlas mapping from /icons/icons.json
 * Converts TexturePacker frames to deck.gl iconMapping format
 */
export const loadIcons = createAsyncThunk(
  'ui/loadIcons',
  async (_, { dispatch }) => {
    const response = await fetch('/icons/icons.json');
    const data = await response.json();
    const mapping = {};
    data.frames.forEach((frame) => {
      mapping[frame.filename] = {
        x: frame.frame.x,
        y: frame.frame.y,
        width: frame.frame.w,
        height: frame.frame.h,
      };
    });
    return mapping;
  }
);

const initialState = {
  drawerNavOpen: false,
  assetInfoOpen: false,
  newProjectDialogOpen: false,
  searchDrawerOpen: false,
  anomalyDrawerOpen: false,
  projectMapFullScreen: false,
  
  // Icon data for asset types
  iconData: {},
  iconsLoaded: false,
  
  // Loading states
  globalLoading: false,
  loadingMessage: '',
  
  // Snackbar notifications handled by notistack
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    initUI: () => initialState,
    
    setDrawerNavOpen: (state, action) => {
      state.drawerNavOpen = action.payload;
    },
    
    toggleDrawerNav: (state) => {
      state.drawerNavOpen = !state.drawerNavOpen;
    },
    
    setAssetInfoOpen: (state, action) => {
      state.assetInfoOpen = action.payload;
    },
    
    setNewProjectDialogOpen: (state, action) => {
      state.newProjectDialogOpen = action.payload;
    },
    
    setSearchDrawerOpen: (state, action) => {
      state.searchDrawerOpen = action.payload;
    },
    
    setAnomalyDrawerOpen: (state, action) => {
      state.anomalyDrawerOpen = action.payload;
    },
    
    setProjectMapFullScreen: (state, action) => {
      state.projectMapFullScreen = action.payload;
    },
    
    toggleProjectMapFullScreen: (state) => {
      state.projectMapFullScreen = !state.projectMapFullScreen;
    },
    
    setIconData: (state, action) => {
      state.iconData = action.payload;
      state.iconsLoaded = true;
    },
    
    addIconData: (state, action) => {
      const { name, data } = action.payload;
      state.iconData[name] = data;
    },
    
    setGlobalLoading: (state, action) => {
      if (typeof action.payload === 'boolean') {
        state.globalLoading = action.payload;
        state.loadingMessage = '';
      } else {
        state.globalLoading = action.payload.loading;
        state.loadingMessage = action.payload.message || '';
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadIcons.fulfilled, (state, action) => {
        state.iconData = action.payload;
        state.iconsLoaded = true;
      });
  },
});

export const {
  initUI,
  setDrawerNavOpen,
  toggleDrawerNav,
  setAssetInfoOpen,
  setNewProjectDialogOpen,
  setSearchDrawerOpen,
  setAnomalyDrawerOpen,
  setProjectMapFullScreen,
  toggleProjectMapFullScreen,
  setIconData,
  addIconData,
  setGlobalLoading,
} = uiSlice.actions;

export default uiSlice.reducer;
